import { ref } from 'vue'
import { apiPost } from '@/composables/useApi'
import { auth } from '@/firebase'

function createRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `pa_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`
}

// Same trimming and length limit the backend applies before saving the reference text.
export function assessmentReferenceText(text) {
  return String(text || '').trim().slice(0, 500)
}

// 额度用完或匿名次数用完后，不再先显示实时分数，免得分数一闪又变成报错
const BLOCKING_ERROR_CODES = ['INSUFFICIENT_USAGE', 'ANONYMOUS_DAILY_LIMIT']

export function usePronunciationAssessment() {
  let scoringBlocked = false
  const assessments = ref(new Map())
  const practiceSummary = ref(null)
  const practiceReport = ref(null)
  const isLoadingAssessments = ref(false)
  const assessmentHistoryError = ref(null)
  const lastAssessmentBilling = ref(null)

  function setAssessment(index, value) {
    const next = new Map(assessments.value)
    next.set(index, value)
    assessments.value = next
  }

  function clearAssessment(index) {
    const next = new Map(assessments.value)
    next.delete(index)
    assessments.value = next
  }

  function hydrateAssessments(latestAssessments = {}) {
    const next = new Map()
    Object.entries(latestAssessments || {}).forEach(([indexKey, assessment]) => {
      const index = Number(indexKey)
      if (!Number.isInteger(index) || !assessment) return
      next.set(index, {
        status: 'done',
        ...assessment,
      })
    })
    assessments.value = next
  }

  async function loadAssessmentHistory({
    audioId,
    audioName = '',
    materialId = '',
    materialBook = '',
    isDemo = false,
  } = {}) {
    if (!audioId || isDemo) {
      clearAssessments()
      practiceSummary.value = null
      practiceReport.value = null
      return
    }

    isLoadingAssessments.value = true
    assessmentHistoryError.value = null

    try {
      const result = await apiPost('/api/pronunciation-practice/audio', {
        audioId,
        audioName,
        materialId,
        materialBook,
        isDemo,
      })
      hydrateAssessments(result.latestAssessments || {})
      practiceSummary.value = result.practice || null
      practiceReport.value = result.report || null
    } catch (error) {
      assessmentHistoryError.value = error.message
    } finally {
      isLoadingAssessments.value = false
    }
  }

  async function assessSubtitleRecording({
    index,
    blob,
    duration,
    liveAssessment,
    referenceText,
    language = 'en-US',
    audioId = '',
    audioName = '',
    materialId = '',
    materialBook = '',
    isDemo = false,
  }) {
    if (index === null || !blob || !referenceText?.trim()) return

    const savedReferenceText = assessmentReferenceText(referenceText)
    setAssessment(index, { status: 'assessing', referenceText: savedReferenceText })

    try {
      const formData = new FormData()
      // Live recognition already scored the attempt: report that result. The recording
      // is only uploaded for scoring when live recognition gave none.
      const reported = await liveAssessment
      if (reported) {
        // 浏览器里的实时识别已经算出分数，先显示出来；扣点和保存记录在后台完成
        if (!scoringBlocked) {
          setAssessment(index, { status: 'done', referenceText: savedReferenceText, language, ...reported })
        }
        formData.append('assessmentResult', JSON.stringify(reported))
        formData.append('durationSeconds', String(duration))
      } else {
        formData.append('audio', blob, `subtitle-${index}.wav`)
      }
      formData.append('subtitleIndex', String(index))
      formData.append('referenceText', referenceText.trim())
      formData.append('language', language)
      formData.append('audioId', audioId || '')
      formData.append('audioName', audioName || '')
      formData.append('materialId', materialId || '')
      formData.append('materialBook', materialBook || '')
      formData.append('isDemo', isDemo ? 'true' : 'false')
      formData.append('requestId', createRequestId())

      const result = await apiPost('/api/pronunciation-assess', formData, { auth: Boolean(auth.currentUser) })
      setAssessment(index, {
        status: 'done',
        referenceText: savedReferenceText,
        pronunciationScore: result.pronunciationScore,
        accuracyScore: result.accuracyScore,
        fluencyScore: result.fluencyScore,
        completenessScore: result.completenessScore,
        language: result.language || language,
        words: result.words || [],
        recognizedText: result.recognizedText,
        usageCharged: result.usageCharged,
        remainingUsage: result.remainingUsage,
        anonymousAttemptsRemaining: result.anonymousAttemptsRemaining,
        anonymousDailyLimit: result.anonymousDailyLimit,
      })
      lastAssessmentBilling.value = {
        usageCharged: result.usageCharged || 0,
        remainingUsage: result.remainingUsage,
        anonymousAttemptsRemaining: result.anonymousAttemptsRemaining,
        anonymousDailyLimit: result.anonymousDailyLimit,
      }
      if (result.savedAssessment?.practiceSummary) {
        practiceSummary.value = result.savedAssessment.practiceSummary
      }
      if (result.savedAssessment?.practiceReport) {
        practiceReport.value = result.savedAssessment.practiceReport
      }
      return { success: true, ...lastAssessmentBilling.value }
    } catch (error) {
      if (BLOCKING_ERROR_CODES.includes(error.code)) scoringBlocked = true
      setAssessment(index, {
        status: 'error',
        referenceText: savedReferenceText,
        error: error.message,
      })
      return { success: false, error: error.message, status: error.status, code: error.code }
    }
  }

  function clearAssessments() {
    assessments.value = new Map()
  }

  return {
    assessments,
    practiceSummary,
    practiceReport,
    isLoadingAssessments,
    assessmentHistoryError,
    lastAssessmentBilling,
    assessSubtitleRecording,
    clearAssessment,
    loadAssessmentHistory,
    clearAssessments,
  }
}
